import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'

import { useExportTableActions } from 'redux/actions/ExportTables'

import { TExportTable } from 'types/GlobalExportTable'

import { useTranslation } from 'language/translator'

import Button from 'components_new/atoms/Button'
import TextField from 'components_new/atoms/TextField'

import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

interface EditExportTableMenuProps {
  editDisabled?: boolean
  exportDisabled?: boolean
  exportTable: TExportTable
  onEdit?: () => void
  onExport?: () => void
}

const EditExportTableMenu = (props: EditExportTableMenuProps) => {
  const {
    editDisabled = true,
    exportDisabled = true,
    exportTable,
    onEdit,
    onExport
  } = props
  const history = useHistory()
  const { t } = useTranslation()

  const { tryUpdateExportTable, tryDeleteExportTable } = useExportTableActions()

  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [newTitle, setNewTitle] = useState(exportTable.title)

  const handleCloseRename = () => {
    setRenameOpen(false)
    setNewTitle(exportTable.title)
  }

  const handleSaveRename = () => {
    tryUpdateExportTable(exportTable.id, {
      data: { title: newTitle }
    })
    setRenameOpen(false)
  }

  const handleConfirmDelete = () => {
    tryDeleteExportTable(exportTable.id, () => {
      history.push('/export-tables')
    })
  }

  return (
    <>
      <OptionsMenuButton
        tooltip={t('global', 'options')}
        MenuProps={{
          maxHeight: 480
        }}
        size="small"
        options={[
          ...(onEdit
            ? [
                {
                  iconName: 'Settings' as const,
                  onClick: onEdit,
                  title: t('global', 'edit'),
                  disabled: editDisabled
                }
              ]
            : []),
          ...(onExport
            ? [
                {
                  iconName: 'Download' as const,
                  onClick: onExport,
                  title: t('global', 'export'),
                  disabled: exportDisabled
                }
              ]
            : []),
          {
            iconName: 'Edit' as const,
            onClick: () => setRenameOpen(true),
            title: t('global', 'change_name')
          },
          {
            iconName: 'Delete' as const,
            onClick: () => setDeleteOpen(true),
            title: t('global', 'delete')
          }
        ]}
      />

      {/* Rename Dialog */}
      <SimpleDialog
        actions={
          <>
            <Button variant="text" onClick={handleCloseRename}>
              {t('global', 'cancel')}
            </Button>
            <Button
              disabled={newTitle.length === 0}
              variant="contained"
              onClick={handleSaveRename}
            >
              {t('global', 'save')}
            </Button>
          </>
        }
        onClose={handleCloseRename}
        open={renameOpen}
        title={t('global', 'change_name')}
      >
        <TextField
          fullWidth={true}
          label={t('global', 'name')}
          autoComplete="off"
          value={newTitle}
          onChange={(event) => setNewTitle(event.target.value)}
          sx={{ mt: 1, mb: 2 }}
          required
        />
      </SimpleDialog>

      {/* Delete Dialog */}
      <SimpleDialog
        title={t('global', 'delete')}
        contentText={
          <>
            <code>{exportTable.title}</code>{' '}
            {t('guides', 'delete_confirmation_suffix')}
          </>
        }
        onClose={() => setDeleteOpen(false)}
        open={deleteOpen}
        actions={
          <>
            <Button onClick={() => setDeleteOpen(false)} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
            >
              {t('global', 'delete')}
            </Button>
          </>
        }
      />
    </>
  )
}

export default EditExportTableMenu
